/**
 * isEqual.js
 *
 * A port of fast-deep-equal/es6 (MIT licence, Evgeny Poberezkin).
 * Logic is intentionally kept as close to the original as possible —
 * readable, flat, and fast.
 *
 */

/** Equality for values that may be NaN (e.g. invalid Date, boxed Number). */
function eqVal(left, right) {
  return left === right || (left !== left && right !== right); // second clause: both NaN
}

export function isEqual(left, right) {
  // Same reference, or same primitive value
  if (left === right) return true;

  // Both must be non-null objects to continue
  if (left && right && typeof left === "object" && typeof right === "object") {
    // Different constructors → never equal
    if (left.constructor !== right.constructor) return false;

    // Arrays
    if (Array.isArray(left)) {
      const len = left.length;
      if (len !== right.length) return false;
      for (let i = 0; i < len; i++) {
        if (!isEqual(left[i], right[i])) return false;
      }
      return true;
    }

    // Date, boxed Number / Boolean / String
    // Objects with a custom valueOf use it for comparison.
    // eqVal handles the NaN case (invalid Date, new Number(NaN)).
    if (left.valueOf !== Object.prototype.valueOf)
      return eqVal(left.valueOf(), right.valueOf());

    // Error & other objects with custom toString
    if (left.toString !== Object.prototype.toString)
      return left.toString() === right.toString();

    // Plain objects & class instances
    const keys = Object.keys(left);
    if (keys.length !== Object.keys(right).length) return false;

    for (let i = 0; i < keys.length; i++) {
      if (!Object.prototype.hasOwnProperty.call(right, keys[i])) return false;
    }

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (!isEqual(left[key], right[key])) return false;
    }

    return true;
  }

  // Handles NaN === NaN for raw primitives
  return left !== left && right !== right;
}
